import React, { useRef, useEffect, useState, useMemo } from 'react';
import { EnhancedTableProps, SortConfig, PaginationConfig } from './types';
import { getColumnWidth, getCellContent, getCellClassName, getSortIndicator } from './utils';
import './EnhancedTable.css';

/**
 * Pagination controls component
 */
function PaginationControls({
  pagination,
  className = ''
}: {
  pagination: PaginationConfig;
  className?: string;
}) {
  const { currentPage, pageSize, totalCount, onPageChange, onPageSizeChange, pageSizeOptions = [25, 50, 100] } = pagination;

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);

  // Generate page numbers with ellipsis for large page ranges
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page + ellipsis if needed
    if (startPage > 1) {
      pageNumbers.push(
        <button
          key="1"
          className="px-3 py-1 border rounded hover:bg-gray-100"
          onClick={() => onPageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1" className="px-1">...</span>);
      }
    }

    // Visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`px-3 py-1 border rounded ${currentPage === i ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Ellipsis + last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="px-1">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          className="px-3 py-1 border rounded hover:bg-gray-100"
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
    <div className={`flex justify-between items-center mt-4 ${className}`}>
      {/* Information about displayed entries */}
      <div className="text-sm text-gray-600">
        {totalCount === 0 ? (
          'No entries to display'
        ) : (
          `Showing ${startRow} to ${endRow} of ${totalCount} entries`
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-2">
        {/* Page size selector */}
        <select
          className="border rounded p-1 text-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size} per page</option>
          ))}
        </select>

        {/* Previous page button */}
        <button
          className={`px-3 py-1 border rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {/* Page number buttons */}
        <div className="flex space-x-1">
          {renderPageNumbers()}
        </div>

        {/* Next page button */}
        <button
          className={`px-3 py-1 border rounded ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/**
 * EnhancedTable component with fixed headers and synchronized horizontal scrolling
 */
function EnhancedTable<T extends Record<string, any>>({
  columns,
  data,
  keyField = 'id',
  defaultSort,
  onSort,
  maxHeight = 'calc(100vh - 16rem)',
  width = '100%',
  className = '',
  stickyHeader = true,
  horizontalScroll = true,
  loading = false,
  emptyMessage = 'No data available',
  rowClassName,
  onRowClick,
  alternateRowColors = false,
  pagination,
  permissions = {}
}: EnhancedTableProps<T>) {
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(defaultSort || null);

  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Synchronize horizontal scrolling between header, body, and top scrollbar
  useEffect(() => {
    const headerWrapper = headerWrapperRef.current;
    const bodyContainer = bodyContainerRef.current;
    const topScrollbar = topScrollbarRef.current;

    if (!headerWrapper || !bodyContainer || !topScrollbar) return;

    const handleBodyScroll = () => {
      // Sync header and top scrollbar with body
      headerWrapper.scrollLeft = bodyContainer.scrollLeft;
      topScrollbar.scrollLeft = bodyContainer.scrollLeft;

      // Check scroll positions for indicators
      const hasMoreRight = bodyContainer.scrollWidth > bodyContainer.clientWidth &&
        bodyContainer.scrollLeft < (bodyContainer.scrollWidth - bodyContainer.clientWidth - 5);
      setHasMoreContent(hasMoreRight);
      setCanScrollRight(hasMoreRight);
      setCanScrollLeft(bodyContainer.scrollLeft > 5);
    };

    const handleTopScroll = () => {
      // Sync body and header with top scrollbar
      bodyContainer.scrollLeft = topScrollbar.scrollLeft;
      headerWrapper.scrollLeft = topScrollbar.scrollLeft;
    };

    // Initial check
    handleBodyScroll();

    bodyContainer.addEventListener('scroll', handleBodyScroll);
    topScrollbar.addEventListener('scroll', handleTopScroll);

    return () => {
      bodyContainer.removeEventListener('scroll', handleBodyScroll);
      topScrollbar.removeEventListener('scroll', handleTopScroll);
    };
  }, [data, columns]);

  // Handle column sorting
  const handleSort = (columnId: string) => {
    if (!onSort) return;

    const newDirection: 'asc' | 'desc' =
      sortConfig?.field === columnId && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc';

    const newSortConfig: SortConfig = { field: columnId, direction: newDirection };
    setSortConfig(newSortConfig);
    onSort(newSortConfig);
  };

  // Generate column styles
  const columnStyles = useMemo(() => {
    return columns.reduce<Record<string, React.CSSProperties>>((styles, column) => {
      styles[column.id] = {
        width: getColumnWidth(column.width),
        minWidth: column.width === 'flexible' ? 'auto' : getColumnWidth(column.width),
        ...column.width === 'flexible' ? { flex: 1 } : {}
      };
      return styles;
    }, {});
  }, [columns]);

  // Get row key
  const getRowKey = (row: T, index: number) => {
    return row[keyField]?.toString() || `row-${index}`;
  };

  // Get row class name
  const getRowClassName = (row: T, index: number) => {
    let baseClass = 'hover:bg-gray-50';

    // Add alternating row colors if enabled
    if (alternateRowColors && index % 2 === 1) {
      baseClass += ' bg-gray-50';
    }

    if (onRowClick) {
      baseClass += ' clickable';
    }

    if (typeof rowClassName === 'function') {
      return `${baseClass} ${rowClassName(row)}`;
    }

    if (rowClassName) {
      return `${baseClass} ${rowClassName}`;
    }

    return baseClass;
  };

  // Filter columns based on permissions
  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      // If column has a visibleWhen function, use it to determine visibility
      if (column.visibleWhen) {
        return column.visibleWhen(permissions);
      }
      // Otherwise, column is always visible
      return true;
    });
  }, [columns, permissions]);

  return (
    <div
      className={`enhanced-table-container ${className} ${hasMoreContent ? 'has-more-content' : ''} ${canScrollLeft ? 'can-scroll-left' : ''} ${canScrollRight ? 'can-scroll-right' : ''}`}
      style={{ width: horizontalScroll ? width : '100%' }}
    >
      {/* Top scrollbar for better UX */}
      {horizontalScroll && (
        <div
          ref={topScrollbarRef}
          className="enhanced-table-top-scrollbar"
        >
          <div
            className="enhanced-table-top-scrollbar-content"
            style={{ width }}
          />
        </div>
      )}
      {/* Header container */}
      {stickyHeader && (
        <div className="enhanced-table-header-container">
          <div
            ref={headerWrapperRef}
            className="enhanced-table-header-wrapper"
          >
            <table className="enhanced-table" style={{ width }}>
              <thead>
                <tr>
                  {visibleColumns.map(column => (
                    <th
                      key={column.id}
                      className={`${getCellClassName(column, true)} ${column.sortable ? 'sortable' : ''}`}
                      style={columnStyles[column.id]}
                      onClick={column.sortable ? () => handleSort(column.id) : undefined}
                      title={typeof column.header === 'string' ? column.header : undefined}
                    >
                      {column.header}
                      {column.sortable && sortConfig && getSortIndicator(column.id, sortConfig.field, sortConfig.direction)}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        </div>
      )}

      {/* Body container */}
      <div
        ref={bodyContainerRef}
        className="enhanced-table-body-container"
        style={{
          maxHeight,
          overflowX: horizontalScroll ? 'auto' : 'hidden'
        }}
      >
        {/* Left scroll indicator */}
        {horizontalScroll && <div className="enhanced-table-scroll-left-indicator" />}

        {/* Right scroll indicator */}
        {horizontalScroll && <div className="enhanced-table-scroll-right-indicator" />}
        {loading ? (
          <div className="enhanced-table-loading">
            <div className="enhanced-table-loading-spinner" />
          </div>
        ) : data.length === 0 ? (
          <div className="enhanced-table-empty">
            {emptyMessage}
          </div>
        ) : (
          <table className="enhanced-table" style={{ width }}>
            {!stickyHeader && (
              <thead>
                <tr>
                  {visibleColumns.map(column => (
                    <th
                      key={column.id}
                      className={`${getCellClassName(column, true)} ${column.sortable ? 'sortable' : ''}`}
                      style={columnStyles[column.id]}
                      onClick={column.sortable ? () => handleSort(column.id) : undefined}
                      title={typeof column.header === 'string' ? column.header : undefined}
                    >
                      {column.header}
                      {column.sortable && sortConfig && getSortIndicator(column.id, sortConfig.field, sortConfig.direction)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {data.map((row, rowIndex) => (
                <tr
                  key={getRowKey(row, rowIndex)}
                  className={getRowClassName(row, rowIndex)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {visibleColumns.map(column => (
                    <td
                      key={`${getRowKey(row, rowIndex)}-${column.id}`}
                      className={getCellClassName(column)}
                      style={columnStyles[column.id]}
                    >
                      {getCellContent(row, column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Horizontal scroll indicator */}
        {horizontalScroll && (
          <div className="enhanced-table-scroll-indicator" />
        )}
      </div>

      {/* Pagination controls */}
      {pagination && (
        <PaginationControls
          pagination={pagination}
          className="px-4 py-2 border-t border-gray-200"
        />
      )}
    </div>
  );
}

export default EnhancedTable;
